import type { ColumnDef, OnChangeFn, RowSelectionState, Table } from "@tanstack/react-table";
import { GenericTable } from "./GenericTable";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader } from "../ui/card";
import { InfoAlert } from "./Alerts";
import { Structure } from "@/types/structure";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Search, X } from "lucide-react";

type DeviceSelectTableProps = {
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelectionState?: RowSelectionState;
  deviceData: Structure[];
};

export default function DeviceSelectTable({
  onRowSelectionChange,
  rowSelectionState,
  deviceData,
}: DeviceSelectTableProps) {
  const columns: ColumnDef<Structure>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected() || (table.getIsSomeRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => {
            table.toggleAllRowsSelected(!!value);
          }}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(checked) => {
            row.toggleSelected(!!checked);
          }}
          aria-label="Select row"
        />
      ),
      enableGlobalFilter: false,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: () => "Device Name",
    },
    {
      accessorKey: "deviceClass",
      header: () => "Device Class",
    },
    {
      accessorKey: "testSiteName",
      header: () => "Test Site Name",
    },
    {
      accessorKey: "nrOfPortCombos",
      header: () => "Number of Port Combos",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="font-bold pb-6">Add Device</div>

        <InfoAlert>
          To link a Device to the Test Label, please select a specification by clicking the
          checkbox.
        </InfoAlert>
      </CardHeader>

      <CardContent>
        <GenericTable
          columns={columns}
          data={deviceData}
          onRowSelectionChange={onRowSelectionChange}
          rowSelection={rowSelectionState}
          toolbarComponent={(props) => <StructuresTableToolbar {...props} />}
        />
      </CardContent>
    </Card>
  );
}

type StructuresTableToolbarProps<TData> = { table: Table<TData> };

function StructuresTableToolbar<TData>({ table }: StructuresTableToolbarProps<TData>) {
  const isGlobalFilterActive = Boolean(table.getState().globalFilter);
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center gap-2">
        <div className="relative flex items-center rounded-md border focus-within:ring-1 focus-within:ring-ring pl-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by Structure Name"
            value={table.getState().globalFilter ?? ""}
            onChange={(event) => table.setGlobalFilter(event.target.value)}
            className="h-8 w-[150px] lg:w-[350px] border-0 focus-visible:ring-0 shadow-none"
          />
        </div>
        {isGlobalFilterActive ? (
          <Button variant="ghost" size="sm" onClick={() => table.resetGlobalFilter()}>
            Clear filter <X />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
